const MAX_DRIVERS = 500;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isNullableString(value, maxLength = 500) {
  return value === null || (typeof value === "string" && value.length <= maxLength);
}

function isOptionalNullableString(value, maxLength = 500) {
  return value === undefined || isNullableString(value, maxLength);
}

function isDateOrNull(value) {
  return value === null || (
    typeof value === "string" &&
    value.length <= 40 &&
    !Number.isNaN(Date.parse(value))
  );
}

function validateMaskedContact(contact) {
  return Boolean(
    contact &&
    typeof contact === "object" &&
    typeof contact.contact_id === "string" &&
    contact.contact_id.length > 0 &&
    typeof contact.channel_type === "string" &&
    typeof contact.owner_type === "string" &&
    isOptionalNullableString(contact.vehicle_ref, 100) &&
    typeof contact.masked_value === "string" &&
    contact.masked_value.length <= 250 &&
    typeof contact.purpose === "string" &&
    typeof contact.is_primary === "boolean" &&
    typeof contact.whatsapp_eligible === "boolean" &&
    (contact.personal_use_consent === null || typeof contact.personal_use_consent === "boolean") &&
    isDateOrNull(contact.consent_verified_at) &&
    typeof contact.valid_from === "string" &&
    !Number.isNaN(Date.parse(contact.valid_from)) &&
    isDateOrNull(contact.valid_to)
  );
}

function validateDirectoryContact(contact) {
  return Boolean(
    contact &&
    typeof contact === "object" &&
    typeof contact.contact_id === "string" &&
    contact.contact_id.length > 0 &&
    typeof contact.channel_type === "string" &&
    typeof contact.owner_type === "string" &&
    isOptionalNullableString(contact.vehicle_ref, 100) &&
    typeof contact.value === "string" &&
    contact.value.length > 0 &&
    contact.value.length <= 500 &&
    typeof contact.purpose === "string" &&
    typeof contact.is_primary === "boolean" &&
    typeof contact.whatsapp_eligible === "boolean" &&
    (contact.personal_use_consent === null || typeof contact.personal_use_consent === "boolean") &&
    isDateOrNull(contact.consent_verified_at) &&
    typeof contact.valid_from === "string" &&
    !Number.isNaN(Date.parse(contact.valid_from)) &&
    isDateOrNull(contact.valid_to)
  );
}

function validateDriver(driver) {
  return Boolean(
    driver &&
    typeof driver === "object" &&
    typeof driver.driver_id === "string" &&
    UUID_PATTERN.test(driver.driver_id) &&
    typeof driver.display_name === "string" &&
    driver.display_name.trim().length > 0 &&
    driver.display_name.length <= 250 &&
    isNullableString(driver.preferred_name, 250) &&
    typeof driver.active === "boolean" &&
    isNullableString(driver.primary_language, 30) &&
    isNullableString(driver.vehicle_ref, 100) &&
    isNullableString(driver.assignment_role, 100) &&
    isNullableString(driver.assignment_status, 100) &&
    isDateOrNull(driver.valid_from) &&
    isDateOrNull(driver.valid_to) &&
    isNullableString(driver.assignment_evidence_ref, 500) &&
    Array.isArray(driver.masked_contacts) &&
    driver.masked_contacts.every(validateMaskedContact)
  );
}

function validateDriverListResponse(data) {
  if (!Array.isArray(data) || data.length > MAX_DRIVERS) return false;
  return data.every(validateDriver);
}

function validateDriverDirectoryResponse(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.drivers) || !Array.isArray(data.vehicle_contacts)) return false;
  if (data.drivers.length > MAX_DRIVERS || data.vehicle_contacts.length > MAX_DRIVERS * 10) return false;
  return data.drivers.every(driver => {
    if (
      !driver ||
      typeof driver.driver_id !== "string" ||
      !UUID_PATTERN.test(driver.driver_id) ||
      typeof driver.display_name !== "string" ||
      driver.display_name.trim().length === 0 ||
      driver.display_name.length > 250 ||
      !isNullableString(driver.preferred_name, 250) ||
      typeof driver.active !== "boolean" ||
      !isNullableString(driver.primary_language, 30) ||
      !isNullableString(driver.vehicle_ref, 100) ||
      !Array.isArray(driver.contacts) ||
      !driver.contacts.every(validateDirectoryContact)
    ) return false;
    return true;
  }) && data.vehicle_contacts.every(validateDirectoryContact);
}

function mergeDriverDirectory(contextDrivers, directory) {
  const contextIds = new Set(contextDrivers.map(driver => driver.driver_id));
  const directoryIds = new Set(directory.drivers.map(driver => driver.driver_id));
  if (contextIds.size !== directoryIds.size || [...contextIds].some(identifier => !directoryIds.has(identifier))) return null;

  return [...contextIds].map(identifier => {
    const contexts = contextDrivers.filter(driver => driver.driver_id === identifier);
    const directoryRows = directory.drivers.filter(driver => driver.driver_id === identifier);
    const fullContacts = new Map(directoryRows.flatMap(driver => driver.contacts).map(contact => [contact.contact_id, contact]));
    const maskedContacts = new Map(contexts.flatMap(driver => driver.masked_contacts).map(contact => [contact.contact_id, contact]));
    const contacts = [...maskedContacts.values()].map(masked => fullContacts.get(masked.contact_id) || {
      ...masked,
      value: masked.masked_value,
      restricted: true,
    });
    fullContacts.forEach((contact, contactId) => {
      if (!maskedContacts.has(contactId)) contacts.push(contact);
    });
    return {
      ...contexts[0],
      assignments: contexts.filter(driver => driver.vehicle_ref || driver.assignment_role || driver.assignment_status),
      contacts,
    };
  });
}

function sortDrivers(drivers, language) {
  return drivers.slice().sort((left, right) => {
    if (left.active !== right.active) return left.active ? -1 : 1;
    return left.display_name.localeCompare(right.display_name, language === "fi" ? "fi" : "en");
  });
}

export {
  sortDrivers,
  mergeDriverDirectory,
  validateDriver,
  validateDirectoryContact,
  validateDriverDirectoryResponse,
  validateDriverListResponse,
  validateMaskedContact,
};
